import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { db } from "../../lib/firebase";
import { doc, getDoc } from "firebase/firestore";
import { ArrowLeft, Video, Image, FileText } from "lucide-react";
import MediaManager from "../../components/MediaManager";
import { MediaType } from "../../types/media";
import { useSubscriptionLimits } from "../../hooks/useSubscriptionLimits";
import QuotaWarning from "../../components/QuotaWarning";
import StorageUsageBar from "../../components/StorageUsageBar";

export default function CourseMedia() {
  const { courseId } = useParams<{ courseId: string }>();
  const [course, setCourse] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<MediaType>("video");
  const {
    getVideoStorageInfo,
    getFileStorageInfo,
    getUpgradeRecommendation,
    userTier,
    loading: limitsLoading,
  } = useSubscriptionLimits();

  const videoInfo = getVideoStorageInfo();
  const fileInfo = getFileStorageInfo();
  const upgradeRec = getUpgradeRecommendation();

  useEffect(() => {
    loadCourse();
  }, [courseId]);

  const loadCourse = async () => {
    if (!courseId) return;

    try {
      const courseDoc = await getDoc(doc(db, "courses", courseId));
      if (courseDoc.exists()) {
        setCourse({ id: courseId, ...courseDoc.data() });
      }
      setLoading(false);
    } catch (error) {
      console.error("Error loading course:", error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl font-bold text-gray-800 mb-4">ไม่พบคอร์ส</p>
          <Link to="/instructor/courses">
            <button className="bg-gradient-to-r from-blue-600 to-pink-600 text-white px-6 py-3 rounded-xl font-semibold">
              กลับไปหน้าคอร์ส
            </button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <Link to="/instructor/courses">
          <button className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6 font-semibold">
            <ArrowLeft className="w-5 h-5" />
            กลับไปหน้าคอร์ส
          </button>
        </Link>

        {/* Header */}
        <div className="bg-white rounded-3xl p-8 shadow-xl mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            จัดการสื่อการเรียนรู้
          </h1>
          <p className="text-gray-600 text-lg">{course.title}</p>
        </div>

        {/* Storage Usage */}
        {!limitsLoading && (
          <div className="bg-white rounded-3xl p-8 shadow-xl mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">การใช้งานพื้นที่จัดเก็บ</h2>
            
            {/* Quota Warnings */}
            {videoInfo && videoInfo.showWarning && (
              <QuotaWarning
                type={videoInfo.percentage >= 100 ? "hard" : "soft"}
                resourceType="video"
                usagePercentage={videoInfo.percentage}
                currentTier={userTier}
                nextTier={upgradeRec?.nextTier}
                upgradeMessage={upgradeRec?.message}
              />
            )}
            
            {fileInfo && fileInfo.showWarning && (
              <QuotaWarning
                type={fileInfo.percentage >= 100 ? "hard" : "soft"}
                resourceType="file"
                usagePercentage={fileInfo.percentage}
                currentTier={userTier}
                nextTier={upgradeRec?.nextTier}
                upgradeMessage={upgradeRec?.message}
              />
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {videoInfo && (
                <StorageUsageBar
                  label="พื้นที่วิดีโอ"
                  used={videoInfo.used}
                  total={videoInfo.total}
                  showWarning={videoInfo.showWarning}
                />
              )}
              {fileInfo && (
                <StorageUsageBar
                  label="พื้นที่ไฟล์เอกสาร"
                  used={fileInfo.used}
                  total={fileInfo.total}
                  showWarning={fileInfo.showWarning}
                />
              )}
            </div>

            <div className="mt-4 text-center">
              <Link to="/instructor/storage" className="text-blue-600 hover:text-blue-700 font-semibold">
                ดูรายละเอียดการใช้งานทั้งหมด →
              </Link>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-3xl p-6 shadow-xl mb-8">
          <div className="flex gap-4 border-b-2 border-gray-200">
            <button
              onClick={() => setActiveTab("video")}
              className={`flex items-center gap-2 px-6 py-3 font-semibold transition-all ${
                activeTab === "video"
                  ? "text-blue-600 border-b-4 border-blue-600 -mb-0.5"
                  : "text-gray-600 hover:text-blue-600"
              }`}
            >
              <Video className="w-5 h-5" />
              วิดีโอ
            </button>
            <button
              onClick={() => setActiveTab("image")}
              className={`flex items-center gap-2 px-6 py-3 font-semibold transition-all ${
                activeTab === "image"
                  ? "text-blue-600 border-b-4 border-blue-600 -mb-0.5"
                  : "text-gray-600 hover:text-blue-600"
              }`}
            >
              <Image className="w-5 h-5" />
              รูปภาพ
            </button>
            <button
              onClick={() => setActiveTab("file")}
              className={`flex items-center gap-2 px-6 py-3 font-semibold transition-all ${
                activeTab === "file"
                  ? "text-blue-600 border-b-4 border-blue-600 -mb-0.5"
                  : "text-gray-600 hover:text-blue-600"
              }`}
            >
              <FileText className="w-5 h-5" />
              ไฟล์ประกอบการสอน
            </button>
          </div>
        </div>

        {/* Media Manager */}
        <MediaManager courseId={courseId!} mediaType={activeTab} />
      </div>
    </div>
  );
}
